import { useState } from "react";
import { BrandLockup } from "@/shell/CandidateFlow";

// F22: the guards bounce a suspended recruiter here with ?error=suspended.
// Keep in step with SUSPENDED_MESSAGE in service/routes.py.
const SUSPENDED_MESSAGE =
  "Your account is suspended. Contact hello@executiveinterview.coach to restore access.";

export function RecruiterLogin() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("error") === "suspended"
      ? SUSPENDED_MESSAGE
      : null,
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      // 1. Fetch a fresh CSRF token from the JSON endpoint.
      const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
      if (!csrfRes.ok) throw new Error("Could not obtain security token. Please refresh and try again.");
      const csrfData = (await csrfRes.json()) as { token?: string; csrf_token?: string };
      const csrfToken = csrfData.token ?? csrfData.csrf_token ?? "";

      // 2. POST as form-encoded — Flask-WTF reads request.form.
      const body = new URLSearchParams();
      body.set("email", email);
      body.set("password", password);
      body.set("csrf_token", csrfToken);

      const res = await fetch("/auth/login", {
        method: "POST",
        credentials: "include",
        redirect: "follow",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": csrfToken,
        },
        body: body.toString(),
      });

      // fetch() with redirect:'follow' follows the redirect automatically.
      // Success: Flask redirects to /coach/recruiter — final URL will contain that path.
      if (res.url && res.url.includes("/coach/recruiter")) {
        window.location.href = "/coach/recruiter";
        return;
      }

      // Correct credentials but email not yet verified — auth_login() still
      // establishes the session, it just redirects elsewhere. Follow it
      // rather than reporting bad credentials.
      if (res.url && res.url.includes("/auth/verify-email-pending")) {
        window.location.href = "/auth/verify-email-pending";
        return;
      }

      // If the status is an explicit 401 / 403 the fetch stops before redirect.
      if (res.status === 401 || res.status === 403) {
        const data = (await res.json().catch(() => null)) as { code?: string; error?: string } | null;
        setError(data?.code === "account_suspended" && data.error ? data.error : "Invalid email or password.");
        setBusy(false);
        return;
      }

      // Any other non-redirect outcome (e.g. re-rendered login page) means bad credentials.
      if (!res.url.includes("/coach/recruiter")) {
        setError("Invalid email or password.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }

    setBusy(false);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FBFAFE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 16,
          padding: 40,
          boxShadow: "0 1px 4px rgba(45,30,90,0.08)",
        }}
      >
        <div style={{ display: "flex", marginBottom: 28 }}>
          <BrandLockup />
        </div>

        {/* Heading */}
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 26,
            fontWeight: 700,
            color: "#17131F",
            letterSpacing: "-0.4px",
            lineHeight: 1.2,
          }}
        >
          Welcome back
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: 14,
            color: "#7C7590",
          }}
        >
          Sign in to continue.
        </p>

        {/* Error alert */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: "12px 14px",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              fontSize: 13.5,
              color: "#B91C1C",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Email field */}
          <div>
            <label
              htmlFor="login-email"
              style={{
                display: "block",
                fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "#7C7590",
                marginBottom: 8,
              }}
            >
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              placeholder="you@firm.com"
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                background: "#FFFFFF",
                border: "1px solid #E0DBD3",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 14,
                color: "#17131F",
                outline: "none",
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Password field */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <label
                htmlFor="login-password"
                style={{
                  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.10em",
                  textTransform: "uppercase",
                  color: "#7C7590",
                }}
              >
                Password
              </label>
              <a
                href="/auth/forgot"
                style={{
                  fontSize: 12.5,
                  color: "#7A6BE0",
                  textDecoration: "none",
                }}
                onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
              >
                Forgot password?
              </a>
            </div>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                background: "#FFFFFF",
                border: "1px solid #E0DBD3",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 14,
                color: "#17131F",
                outline: "none",
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={busy || !email || !password}
            style={{
              width: "100%",
              height: 48,
              background: busy || !email || !password ? "#4B5563" : "#17131F",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 9999,
              fontSize: 15,
              fontWeight: 600,
              cursor: busy || !email || !password ? "default" : "pointer",
              opacity: busy || !email || !password ? 0.6 : 1,
              transition: "opacity 0.12s, background 0.12s",
              letterSpacing: "0.01em",
            }}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {/* Sign up link */}
        <p
          style={{
            marginTop: 24,
            textAlign: "center",
            fontSize: 13.5,
            color: "#7C7590",
          }}
        >
          Don&apos;t have an account?{" "}
          <a
            href="/auth/register"
            style={{
              color: "#7A6BE0",
              textDecoration: "none",
              fontWeight: 500,
            }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Sign up for the beta
          </a>
        </p>
      </div>
    </div>
  );
}
